import {useCallback} from 'react'
import {ModelPickerDesign} from '@github-ui/copilot-chat/components/ModelPicker'
import {useAgentModels} from '../../hooks/useAgentModels'
import type {CopilotChatModel} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'

export const AUTO_MODEL_ID = 'auto'

interface ModelPickerProps {
  onUpdateModel: (modelId: string) => void
  selectedModelId?: string
}

function createPlaceholderModel(invalidModelId: string): CopilotChatModel {
  return {
    id: invalidModelId,
    name: `${invalidModelId} (unavailable)`,
    displayName: `${invalidModelId} (unavailable)`,
    hasLimitedCapabilities: true,
    isThirdParty: false,
    version: '',
    vendor: '',
    preview: false,
    model_picker_enabled: false,
    capabilities: {
      type: 'chat',
      family: '',
      limits: {max_prompt_tokens: 0},
      supports: {},
      tokenizer: '',
    },
  }
}

export function ModelPicker({onUpdateModel, selectedModelId}: ModelPickerProps) {
  const {models: availableModels, isLoading} = useAgentModels()

  const handleUpdateModel = useCallback(
    (model: CopilotChatModel) => {
      onUpdateModel(model.id)
    },
    [onUpdateModel],
  )

  const foundModel = availableModels?.find(model => model.id === selectedModelId)
  const defaultModel = availableModels?.find(m => m.is_chat_default) ?? availableModels?.[0]

  // If no model selected yet, use default
  const selectedModel = selectedModelId ? (foundModel ?? createPlaceholderModel(selectedModelId)) : defaultModel

  const isOnlyAutoModel =
    copilotFeatureFlags.codingAgentHideModelPickerIfOnlyAuto &&
    availableModels?.length === 1 &&
    availableModels[0]?.id === AUTO_MODEL_ID

  if (isLoading || !availableModels || !selectedModel || isOnlyAutoModel) return null

  return (
    <ModelPickerDesign
      models={availableModels}
      selectedModel={selectedModel}
      onModelPicked={handleUpdateModel}
      disabled={false}
      type={'global'}
      variant={'invisible'}
      showCategories={false}
      showMultipliers
      showPreviewModelsSeparately={false}
    />
  )
}
